import { whenever } from '@vueuse/core';
import { computed, onScopeDispose, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import useSsoPromptState from '@/composables/useSsoPromptState';
import { ls } from '@/local_storage';
import { handleLogout } from '@/utils/router';
import { lookupSsoByEmail } from '@/utils/ssoEmailLookup';

const CHECK_BACKSTOP_MS = 5000;
const PENDING_CHECK_TTL_MS = 30 * 60 * 1000;

export default function useGoogleSsoPostLoginPrompt() {
  const { currentUser, isLoggedIn } = useAuth();
  const { ssoEmailMatch, isPromptVisible, resetPrompt } = useSsoPromptState();

  const pendingSince = ls.pendingGoogleSsoCheck.get();
  ls.pendingGoogleSsoCheck.set();
  const isPendingGoogleSso =
    !!pendingSince && Date.now() - pendingSince < PENDING_CHECK_TTL_MS;

  const isLookupPending = ref(isPendingGoogleSso);
  const isChecking = computed(() => isLookupPending.value && isLoggedIn.value);
  const isRedirecting = ref(false);

  let backstopTimeoutId: ReturnType<typeof setTimeout> | undefined;
  if (isLookupPending.value) {
    backstopTimeoutId = setTimeout(() => {
      isLookupPending.value = false;
    }, CHECK_BACKSTOP_MS);
  }
  onScopeDispose(() => {
    if (backstopTimeoutId) clearTimeout(backstopTimeoutId);
  });

  whenever(
    () => !!currentUser.value?.email && isLookupPending.value,
    async () => {
      try {
        const result = await lookupSsoByEmail(currentUser.value.email);
        if (result && isLookupPending.value) {
          ssoEmailMatch.value = result;
          isPromptVisible.value = true;
        }
      } finally {
        if (backstopTimeoutId) clearTimeout(backstopTimeoutId);
        isLookupPending.value = false;
      }
    },
    { immediate: true },
  );

  function buildSsoLoginUrl(ssoConfigId: string) {
    const query = new URLSearchParams({ sso: ssoConfigId });
    const destination = window.location.pathname + window.location.search;
    if (destination && destination !== '/') query.set('next', destination);
    return `/login?${query}`;
  }

  function handleContinueWithSso() {
    if (!ssoEmailMatch.value) return;
    isRedirecting.value = true;
    handleLogout(buildSsoLoginUrl(ssoEmailMatch.value.ssoConfigId));
  }

  return {
    isPromptVisible,
    ssoEmailMatch,
    isChecking,
    isRedirecting,
    handleContinueWithSso,
    handleContinueWithPassword: resetPrompt,
  };
}
